// ============================================================
//  RightPanel — unified right sidebar
//  Tab 1: Blocks (draggable palette)
//  Tab 2: Properties (settings for selected block)
//  Auto-switches to Properties when a block is selected.
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import { useEditor, Element } from '@craftjs/core';
import { BLOCK_CATALOG } from '../../../types/index';
import type { BlockType, HeaderState } from '../../../types/index';
import {
  Titre,
  Texte,
  Image,
  Bouton,
  Separateur,
  Espaceur,
  Section,
  Cta,
  Libre,
  Formulaire,
  Pricing,
  Offre,
  ValidationBlock,
  FormFieldInput,
  FormFieldTextarea,
  FormFieldRadio,
  FormFieldCheckbox,
  FormFieldSelect,
  FormContainer,
} from '../blocks/UserBlocks';
import { SettingsPanel } from '../panels/SettingsPanel';

const COMPONENT_MAP: Record<
  BlockType,
  React.ComponentType<Record<string, unknown>>
> = {
  titre: Titre as React.ComponentType<Record<string, unknown>>,
  texte: Texte as React.ComponentType<Record<string, unknown>>,
  image: Image as React.ComponentType<Record<string, unknown>>,
  bouton: Bouton as React.ComponentType<Record<string, unknown>>,
  separateur: Separateur as React.ComponentType<Record<string, unknown>>,
  espaceur: Espaceur as React.ComponentType<Record<string, unknown>>,
  section: Section as React.ComponentType<Record<string, unknown>>,
  cta: Cta as React.ComponentType<Record<string, unknown>>,
  libre: Libre as React.ComponentType<Record<string, unknown>>,
  formulaire: Formulaire as React.ComponentType<Record<string, unknown>>,
  pricing: Pricing as React.ComponentType<Record<string, unknown>>,
  offre: Offre as React.ComponentType<Record<string, unknown>>,
  validation: ValidationBlock as React.ComponentType<Record<string, unknown>>,
  'form-input': FormFieldInput as React.ComponentType<Record<string, unknown>>,
  'form-textarea': FormFieldTextarea as React.ComponentType<
    Record<string, unknown>
  >,
  'form-radio': FormFieldRadio as React.ComponentType<Record<string, unknown>>,
  'form-checkbox': FormFieldCheckbox as React.ComponentType<
    Record<string, unknown>
  >,
  'form-select': FormFieldSelect as React.ComponentType<
    Record<string, unknown>
  >,
  'form-container': FormContainer as React.ComponentType<
    Record<string, unknown>
  >,
};

interface Props {
  onAddBlock: (type: BlockType) => void;
  selectedHeader: boolean;
  header: HeaderState;
  onUpdateHeader: (p: Partial<HeaderState>) => void;
}

export function RightPanel({
  onAddBlock,
  selectedHeader,
  header,
  onUpdateHeader,
}: Props): React.ReactElement {
  const [activeTab, setActiveTab] = useState<'blocks' | 'properties'>('blocks');
  const [filter, setFilter] = useState('');
  const { connectors, selected } = useEditor((state) => {
    const nodeId = state.events.selected
      ? Array.from(state.events.selected)[0]
      : null;
    return { selected: nodeId };
  });

  // Auto-switch tab when a block or header is selected/deselected
  useEffect(() => {
    if (selected != null || selectedHeader) {
      setActiveTab('properties');
    } else {
      setActiveTab('blocks');
    }
  }, [selected, selectedHeader]);

  const groups = Array.from(new Set(BLOCK_CATALOG.map((b) => b.group)));

  const tabStyle = (tab: 'blocks' | 'properties'): React.CSSProperties => ({
    flex: 1,
    padding: '9px 0',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: activeTab === tab ? 'var(--accent)' : 'var(--muted)',
    borderBottom:
      activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  });

  return (
    <aside
      style={{
        width: 280,
        background: 'var(--surface)',
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Tab headers */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <button
          style={tabStyle('blocks')}
          onClick={() => setActiveTab('blocks')}
        >
          Blocs
        </button>
        <button
          style={tabStyle('properties')}
          onClick={() => setActiveTab('properties')}
        >
          Propriétés
        </button>
      </div>

      {/* Tab: Blocks */}
      {activeTab === 'blocks' && (
        <>
          <div
            style={{
              padding: '10px 12px 8px',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}
          >
            <input
              className="field-input"
              placeholder="Rechercher un bloc…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
            {groups.map((group) => {
              const items = BLOCK_CATALOG.filter(
                (b) =>
                  b.group === group &&
                  (!filter ||
                    b.name.toLowerCase().includes(filter.toLowerCase()) ||
                    b.desc.toLowerCase().includes(filter.toLowerCase()))
              );
              if (!items.length) return null;
              return (
                <div key={group}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--muted)',
                      padding: '8px 4px 6px',
                    }}
                  >
                    {group}
                  </div>
                  {items.map((b) => {
                    const Comp = COMPONENT_MAP[b.type];
                    return (
                      <PaletteItem
                        key={b.type}
                        entry={b}
                        connectorRef={(ref) => {
                          if (ref)
                            connectors.create(ref, <Element is={Comp} />);
                        }}
                        onAdd={() => onAddBlock(b.type)}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Tab: Properties — rendered by SettingsPanel */}
      {activeTab === 'properties' && (
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SettingsPanel
            selectedHeader={selectedHeader}
            header={header}
            onUpdateHeader={onUpdateHeader}
            inline
          />
        </div>
      )}
    </aside>
  );
}

// ── Palette item ──────────────────────────────────────────

interface PaletteItemProps {
  entry: typeof BLOCK_CATALOG[number];
  connectorRef: (ref: HTMLElement | null) => void;
  onAdd: () => void;
}

function PaletteItem({
  entry: b,
  connectorRef,
  onAdd,
}: PaletteItemProps): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={(el) => {
        if (el) {
          (ref as React.MutableRefObject<HTMLElement | null>).current = el;
          connectorRef(el);
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 10px',
        borderRadius: 8,
        cursor: 'grab',
        border: '1px solid transparent',
        marginBottom: 2,
        transition: 'all 0.15s',
        userSelect: 'none',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'var(--surface2)';
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = '';
        (e.currentTarget as HTMLElement).style.borderColor = 'transparent';
      }}
      title={b.desc}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          flexShrink: 0,
          background: b.metier ? 'var(--teal-dim)' : 'var(--surface2)',
        }}
      >
        {b.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
          {b.name}
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {b.desc}
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        style={{
          width: 22,
          height: 22,
          borderRadius: 4,
          background: 'var(--accent-dim)',
          color: 'var(--accent)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        title="Ajouter"
      >
        +
      </button>
    </div>
  );
}
